/**
 * Enmon brand theme — light.
 *
 * Plain data, owned here. Paste a fresh Theme Buddy export straight into the object below and let
 * `satisfies ThemeConfig` do the reviewing: antd only accepts a subset of what Theme Buddy emits, so
 * anything it refuses lights up red in the editor and is deleted on the spot.
 *
 * Two things worth knowing before deleting a flagged key (both hit us on the first import):
 * - antd derives a lot of tokens internally and refuses them as overrides. Those values simply cannot
 *   be set; they follow from the tokens they are derived from.
 * - Theme Buddy sometimes files a token under the wrong component. `groupBorderColor` is an Avatar
 *   token, `handleFontSize` an InputNumber one. Move it rather than drop it.
 */
import type { ThemeConfig } from 'antd';

export const lightTheme = {
  token: {
    colorText: 'rgba(12, 12, 13, 0.88)',
    colorTextSecondary: 'rgba(12, 12, 13, 0.65)',
    colorTextTertiary: 'rgba(12, 12, 13, 0.45)',
    colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
    colorTextLightSolid: '#ffffff',
    colorTextHeading: 'rgba(12, 12, 13, 0.88)',
    colorTextLabel: 'rgba(12, 12, 13, 0.65)',
    colorTextDescription: 'rgba(12, 12, 13, 0.45)',
    colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
    colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
    colorIcon: 'rgba(12, 12, 13, 0.45)',
    colorIconHover: 'rgba(12, 12, 13, 0.88)',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#f5f5f5',
    colorBgMask: 'rgba(12, 12, 13, 0.45)',
    colorBgSpotlight: 'rgba(12, 12, 13, 0.85)',
    colorBorder: '#ced2d9',
    colorBorderSecondary: '#e4e8f0',
    colorFill: 'rgba(12, 12, 13, 0.15)',
    colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
    colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
    colorFillQuaternary: 'rgba(12, 12, 13, 0.02)',
    colorWhite: '#ffffff',
    colorBgBase: '#ffffff',
    colorTextBase: '#000000',
    colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
    colorBgTextActive: 'rgba(12, 12, 13, 0.15)',
    colorBgTextHover: 'rgba(12, 12, 13, 0.06)',
    colorBorderBg: '#ffffff',
    colorFillContent: 'rgba(12, 12, 13, 0.06)',
    colorFillContentHover: 'rgba(12, 12, 13, 0.15)',
    colorFillAlter: 'rgba(12, 12, 13, 0.02)',
    colorSplit: 'rgba(12, 12, 13, 0.06)',
    colorPrimary: '#126e69',
    colorSuccess: '#78c41a',
    colorWarning: '#fa8816',
    colorInfo: '#126e69',
    colorError: '#f24c4e',
    colorLink: '#126e69',
    colorErrorBg: '#ffe5e5',
    colorErrorBgHover: '#ffdfdf',
    colorErrorBorder: '#ffb2b2',
    colorErrorBorderHover: '#ff9999',
    colorErrorHover: '#ff9999',
    colorErrorActive: '#e61723',
    colorErrorTextHover: '#f24c4e',
    colorErrorText: '#ff6666',
    colorErrorTextActive: '#e61723',
    colorLinkHover: '#1ba39c',
    colorInfoBg: '#d3f8f6',
    colorInfoBgHover: '#bdf0ed',
    colorInfoBorder: '#8ae4df',
    colorInfoBorderHover: '#23cdc4',
    colorInfoHover: '#1ba39c',
    colorInfoActive: '#126e69',
    colorInfoTextHover: '#1ba39c',
    colorInfoText: '#168882',
    colorInfoTextActive: '#126e69',
    colorLinkActive: '#126e69',
    colorPrimaryBg: '#d3f8f6',
    colorPrimaryBgHover: '#bdf0ed',
    colorPrimaryBorder: '#8ae4df',
    colorPrimaryBorderHover: '#23cdc4',
    colorPrimaryHover: '#1ba39c',
    colorPrimaryActive: '#126e69',
    colorPrimaryTextHover: '#1ba39c',
    colorPrimaryText: '#168882',
    colorPrimaryTextActive: '#126e69',
    colorSuccessBg: '#f7ffed',
    colorSuccessBgHover: '#ddf7be',
    colorSuccessBorder: '#c2eb8f',
    colorSuccessBorderHover: '#a7de64',
    colorSuccessHover: '#a7de64',
    colorSuccessActive: '#5d9e0d',
    colorSuccessTextHover: '#8ed13d',
    colorSuccessText: '#78c41a',
    colorSuccessTextActive: '#5d9e0d',
    colorWarningBg: '#fff3e6',
    colorWarningBgHover: '#ffddba',
    colorWarningBorder: '#ffc891',
    colorWarningBorderHover: '#ffb469',
    colorWarningHover: '#ffb469',
    colorWarningActive: '#d46e08',
    colorWarningTextHover: '#ff9f40',
    colorWarningText: '#fa8816',
    colorWarningTextActive: '#d46e08',
    colorErrorOutline: 'rgba(255, 6, 6, 0.06)',
    colorWarningOutline: 'rgba(255, 130, 5, 0.1)',
    controlItemBgActive: '#d3f8f6',
    controlItemBgActiveDisabled: 'rgba(12, 12, 13, 0.15)',
    controlItemBgActiveHover: '#bdf0ed',
    controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
    controlOutline: 'rgba(64, 217, 255, 0.1)',
    controlTmpOutline: 'rgba(12, 12, 13, 0.02)',
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,
    sizeStep: 4,
    sizeUnit: 4,
    controlInteractiveSize: 16,
    size: 16,
    sizeLG: 24,
    sizeMD: 20,
    sizeMS: 16,
    sizeSM: 12,
    sizeXL: 32,
    sizeXS: 8,
    sizeXXL: 48,
    controlHeight: 32,
    sizeXXS: 4,
    controlHeightLG: 40,
    controlHeightSM: 24,
    controlHeightXS: 16,
    lineWidth: 1,
    lineWidthBold: 2,
    lineWidthFocus: 4,
    controlOutlineWidth: 2,
    screenLG: 992,
    screenLGMax: 1199,
    screenLGMin: 992,
    screenMD: 768,
    screenMDMax: 991,
    screenMDMin: 768,
    screenSM: 576,
    screenSMMax: 767,
    screenSMMin: 576,
    screenXL: 1200,
    screenXLMax: 1599,
    screenXLMin: 1200,
    screenXS: 480,
    screenXSMax: 575,
    screenXSMin: 480,
    screenXXL: 1600,
    screenXXLMin: 1600,
    sizePopupArrow: 16,
    margin: 16,
    marginLG: 24,
    marginMD: 20,
    marginSM: 12,
    marginXL: 32,
    marginXS: 8,
    marginXXL: 48,
    marginXXS: 4,
    padding: 16,
    paddingLG: 24,
    paddingMD: 20,
    paddingSM: 12,
    paddingXL: 32,
    paddingXS: 8,
    paddingXXS: 4,
    paddingContentHorizontal: 16,
    paddingContentHorizontalLG: 24,
    paddingContentHorizontalSM: 16,
    paddingContentVertical: 12,
    paddingContentVerticalLG: 16,
    paddingContentVerticalSM: 8,
    controlPaddingHorizontal: 12,
    controlPaddingHorizontalSM: 8,
    fontFamily: 'Poppins',
    fontFamilyCode: 'Roboto Mono',
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    fontSizeXL: 20,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    lineHeight: 1.5714285714285714,
    lineHeightHeading1: 1.2105263157894737,
    lineHeightHeading2: 1.2666666666666666,
    lineHeightHeading3: 1.3333333333333333,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.5,
    lineHeightLG: 1.5,
    lineHeightSM: 1.6666666666666667,
    fontSizeIcon: 12,
    fontWeightStrong: 600,
    colorBgSolid: '#0c0c0d',
    colorBgSolidActive: 'rgba(12, 12, 13, 0.95)',
    colorBgSolidHover: 'rgba(12, 12, 13, 0.75)',
  },
  components: {
    Input: {
      paddingInlineSM: 7,
      paddingInlineLG: 11,
      paddingInline: 11,
      paddingBlockSM: 0,
      paddingBlockLG: 7,
      paddingBlock: 4,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(255, 130, 5, 0.1)',
      colorWarningBorderHover: '#ffb469',
      colorWarning: '#fa8816',
      colorTextTertiary: 'rgba(12, 12, 13, 0.45)',
      colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimary: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorErrorOutline: 'rgba(255, 6, 6, 0.06)',
      colorErrorBorderHover: '#ff9999',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#1ba39c',
      addonBg: 'rgba(12, 12, 13, 0.02)',
      activeBorderColor: '#126e69',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorErrorBgHover: '#ffdfdf',
      colorErrorBg: '#ffe5e5',
      colorWarningBg: '#fff3e6',
      colorWarningBgHover: '#ffddba',
      colorWarningText: '#fa8816',
      colorErrorText: '#ff6666',
      activeBg: '#ffffff',
      hoverBg: '#ffffff',
      inputFontSize: 14,
      inputFontSizeLG: 16,
      inputFontSizeSM: 12,
      fontFamily: 'Poppins',
    },
    Transfer: {
      listWidthLG: 250,
      listWidth: 180,
      listHeight: 200,
      itemPaddingBlock: 5,
      paddingXS: 8,
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActiveHover: '#bdf0ed',
      controlItemBgActive: '#d3f8f6',
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusLG: 8,
      colorWarning: '#fa8816',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorLinkHover: '#1ba39c',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      itemHeight: 32,
      headerHeight: 40,
      fontFamily: 'Poppins',
    },
    Segmented: {
      paddingXXS: 4,
      marginSM: 12,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextLabel: 'rgba(12, 12, 13, 0.65)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorFill: 'rgba(12, 12, 13, 0.15)',
      colorBgLayout: '#f5f5f5',
      colorBgElevated: '#ffffff',
      itemSelectedColor: 'rgba(12, 12, 13, 0.88)',
      itemSelectedBg: '#ffffff',
      itemHoverColor: 'rgba(12, 12, 13, 0.88)',
      itemHoverBg: 'rgba(12, 12, 13, 0.06)',
      itemColor: 'rgba(12, 12, 13, 0.65)',
      itemActiveBg: 'rgba(12, 12, 13, 0.15)',
      trackPadding: 2,
      trackBg: '#f5f5f5',
      fontFamily: 'Poppins',
    },
    Switch: {
      trackPadding: 2,
      trackMinWidthSM: 28,
      trackMinWidth: 44,
      trackHeightSM: 16,
      trackHeight: 22,
      handleSizeSM: 12,
      handleSize: 18,
      marginXXS: 4,
      lineWidthFocus: 4,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      controlHeight: 32,
      colorWhite: '#ffffff',
      colorTextTertiary: 'rgba(12, 12, 13, 0.45)',
      colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      handleBg: '#ffffff',
      fontFamily: 'Poppins',
      innerMaxMargin: 24,
      innerMaxMarginSM: 18,
      innerMinMargin: 9,
      innerMinMarginSM: 6,
    },
    Timeline: {
      itemPaddingBottom: 20,
      paddingXXS: 4,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      margin: 16,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSize: 14,
      controlHeightLG: 40,
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorPrimary: '#126e69',
      colorError: '#f24c4e',
      colorBgContainer: '#ffffff',
      tailColor: 'rgba(12, 12, 13, 0.06)',
      dotBg: '#ffffff',
      fontFamily: 'Poppins',
      tailWidth: 2,
      paddingSM: 12,
    },
    Tabs: {
      horizontalItemGutter: 32,
      cardGutter: 2,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      margin: 16,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimaryActive: '#126e69',
      colorPrimary: '#126e69',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorBorderSecondary: '#e4e8f0',
      colorBorder: '#ced2d9',
      colorBgContainer: '#ffffff',
      itemSelectedColor: '#126e69',
      itemHoverColor: '#1ba39c',
      itemColor: 'rgba(12, 12, 13, 0.88)',
      itemActiveColor: '#126e69',
      inkBarColor: '#126e69',
      cardHeight: 40,
      cardBg: 'rgba(12, 12, 13, 0.02)',
      titleFontSize: 14,
      titleFontSizeLG: 16,
      titleFontSizeSM: 14,
      fontFamily: 'Poppins',
      cardHeightLG: 48,
    },
    Table: {
      stickyScrollBarBorderRadius: 100,
      headerSplitColor: '#e4e8f0',
      headerBg: '#fafafa',
      footerBg: '#fafafa',
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 500,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActiveHover: '#bdf0ed',
      controlItemBgActive: '#d3f8f6',
      controlInteractiveSize: 16,
      controlHeight: 32,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimary: '#126e69',
      colorLinkHover: '#1ba39c',
      colorLinkActive: '#126e69',
      colorLink: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorFillContent: 'rgba(12, 12, 13, 0.06)',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorBorderSecondary: '#e4e8f0',
      colorBgContainer: '#ffffff',
      rowSelectedHoverBg: '#bdf0ed',
      headerFilterHoverBg: 'rgba(12, 12, 13, 0.06)',
      headerColor: 'rgba(12, 12, 13, 0.88)',
      headerBorderRadius: 8,
      footerColor: 'rgba(12, 12, 13, 0.88)',
      filterDropdownMenuBg: '#ffffff',
      filterDropdownBg: '#ffffff',
      cellPaddingInlineSM: 8,
      cellPaddingInlineMD: 8,
      cellPaddingInline: 16,
      cellPaddingBlockSM: 8,
      cellPaddingBlockMD: 12,
      cellPaddingBlock: 16,
      borderColor: '#e4e8f0',
      rowHoverBg: '#fafafa',
      headerSortActiveBg: '#ececec',
      bodySortBg: '#fafafa',
      headerSortHoverBg: '#ececec',
      cellFontSize: 14,
      cellFontSizeMD: 14,
      cellFontSizeSM: 14,
      fontFamily: 'Poppins',
      expandIconBg: '#ffffff',
      rowExpandedBg: '#fafafa',
      rowSelectedBg: '#d3f8f6',
      selectionColumnWidth: 32,
      stickyScrollBarBg: 'rgba(12, 12, 13, 0.25)',
    },
    Steps: {
      dotSize: 8,
      dotCurrentSize: 10,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSizeHeading3: 24,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlHeightLG: 40,
      borderRadiusSM: 4,
      colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorTextLabel: 'rgba(12, 12, 13, 0.65)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorFillContent: 'rgba(12, 12, 13, 0.06)',
      colorError: '#f24c4e',
      colorBorderSecondary: '#e4e8f0',
      colorBorderBg: '#ffffff',
      colorBgContainer: '#ffffff',
      titleLineHeight: 32,
      iconSizeSM: 24,
      iconSize: 32,
      customIconFontSize: 24,
      iconFontSize: 14,
      fontFamily: 'Poppins',
      navArrowColor: 'rgba(12, 12, 13, 0.25)',
      borderRadius: 6,
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorPrimaryBg: '#d3f8f6',
      colorErrorBg: '#ffe5e5',
      colorPrimaryHover: '#1ba39c',
      colorErrorHover: '#ff9999',
    },
    Spin: {
      dotSizeSM: 14,
      dotSize: 20,
      contentHeight: 400,
      marginXXS: 4,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightLG: 40,
      controlHeight: 32,
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimary: '#126e69',
      colorBgContainer: '#ffffff',
      dotSizeLG: 32,
      fontFamily: 'Poppins',
    },
    Slider: {
      railSize: 4,
      handleSizeHover: 12,
      dotSize: 8,
      controlSize: 10,
      handleSize: 10,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryBorderHover: '#23cdc4',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorFillContentHover: 'rgba(12, 12, 13, 0.15)',
      colorBorderSecondary: '#e4e8f0',
      colorBgElevated: '#ffffff',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      trackHoverBg: '#23cdc4',
      trackBgDisabled: 'rgba(12, 12, 13, 0.04)',
      trackBg: '#8ae4df',
      railHoverBg: 'rgba(12, 12, 13, 0.06)',
      railBg: 'rgba(12, 12, 13, 0.04)',
      handleColor: '#8ae4df',
      handleActiveColor: '#126e69',
      dotBorderColor: '#e4e8f0',
      dotActiveBorderColor: '#8ae4df',
      handleColorDisabled: '#bfbfbf',
      fontFamily: 'Poppins',
      handleActiveOutlineColor: 'rgba(22, 119, 255, 0.2)',
      handleLineWidth: 2,
      handleLineWidthHover: 2.5,
    },
    Rate: {
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightLG: 40,
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorFillContent: 'rgba(12, 12, 13, 0.06)',
      fontFamily: 'Poppins',
      starColor: '#fae314',
      starBg: 'rgba(12, 12, 13, 0.06)',
      starSize: 20,
      starSizeLG: 25,
      starSizeSM: 15,
    },
    Radio: {
      radioSize: 16,
      dotSize: 8,
      buttonPaddingInline: 15,
      paddingXS: 8,
      padding: 16,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlItemBgActiveDisabled: 'rgba(12, 12, 13, 0.15)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimaryActive: '#126e69',
      colorPrimary: '#126e69',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      wrapperMarginInlineEnd: 8,
      dotColorDisabled: 'rgba(12, 12, 13, 0.25)',
      buttonSolidCheckedHoverBg: '#1ba39c',
      buttonSolidCheckedColor: '#ffffff',
      buttonSolidCheckedBg: '#126e69',
      buttonSolidCheckedActiveBg: '#126e69',
      buttonColor: 'rgba(12, 12, 13, 0.88)',
      buttonCheckedColorDisabled: 'rgba(12, 12, 13, 0.25)',
      buttonCheckedBgDisabled: 'rgba(12, 12, 13, 0.15)',
      buttonCheckedBg: '#ffffff',
      buttonBg: '#ffffff',
      fontFamily: 'Poppins',
    },
    Popover: {
      titleMinWidth: 177,
      sizePopupArrow: 16,
      paddingSM: 12,
      padding: 16,
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSize: 14,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusLG: 8,
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorBgElevated: '#ffffff',
      fontFamily: 'Poppins',
    },
    Notification: {
      width: 384,
      paddingMD: 20,
      paddingLG: 24,
      paddingContentHorizontalLG: 24,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlHeightLG: 40,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorInfo: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorError: '#f24c4e',
      colorBgElevated: '#ffffff',
      lineHeightLG: 1.5,
      lineWidthFocus: 4,
      fontFamily: 'Poppins',
    },
    Tooltip: {
      paddingSM: 12,
      sizePopupArrow: 16,
      paddingXS: 8,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadius: 6,
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorBgSpotlight: 'rgba(12, 12, 13, 0.85)',
      fontFamily: 'Poppins',
      maxWidth: 250,
    },
    Menu: {
      subMenuItemBg: 'rgba(12, 12, 13, 0.02)',
      darkSubMenuItemBg: '#000c17',
      darkItemDisabledColor: 'rgba(255, 255, 255, 0.25)',
      darkItemColor: 'rgba(255, 255, 255, 0.65)',
      darkGroupTitleColor: 'rgba(255, 255, 255, 0.65)',
      darkItemBg: '#001529',
      paddingXS: 8,
      paddingXL: 32,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      margin: 16,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgActive: '#d3f8f6',
      controlHeightSM: 24,
      controlHeightLG: 40,
      borderRadius: 6,
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorFillContent: 'rgba(12, 12, 13, 0.06)',
      colorErrorHover: '#ff9999',
      colorErrorBg: '#ffe5e5',
      colorError: '#f24c4e',
      colorBgElevated: '#ffffff',
      colorBgContainer: '#ffffff',
      subMenuItemBorderRadius: 4,
      popupBg: '#ffffff',
      itemSelectedColor: '#126e69',
      itemSelectedBg: '#d3f8f6',
      itemHoverColor: 'rgba(12, 12, 13, 0.88)',
      itemHoverBg: 'rgba(12, 12, 13, 0.06)',
      itemHeight: 40,
      itemDisabledColor: 'rgba(12, 12, 13, 0.25)',
      itemColor: 'rgba(12, 12, 13, 0.88)',
      itemBorderRadius: 8,
      itemBg: '#ffffff',
      iconSize: 16,
      horizontalItemSelectedColor: '#126e69',
      horizontalItemSelectedBg: 'rgba(0, 0, 0, 0)',
      horizontalItemHoverColor: '#126e69',
      horizontalItemHoverBg: 'rgba(0, 0, 0, 0)',
      groupTitleColor: 'rgba(12, 12, 13, 0.45)',
      darkItemSelectedColor: '#ffffff',
      darkItemSelectedBg: '#126e69',
      darkItemHoverColor: '#ffffff',
      darkItemHoverBg: 'rgba(0, 0, 0, 0)',
      collapsedIconSize: 16,
      darkPopupBg: '#001529',
      activeBarBorderWidth: 1,
      collapsedWidth: 80,
      dropdownWidth: 160,
      itemMarginBlock: 4,
      itemMarginInline: 4,
      iconMarginInlineEnd: 10,
      groupTitleFontSize: 14,
      groupTitleLineHeight: 1.5714285714285714,
      fontFamily: 'Poppins',
      subMenuItemSelectedColor: '#126e69',
      itemPaddingInline: 16,
      horizontalLineHeight: 46,
      itemActiveBg: '#d3f8f6',
      dangerItemActiveBg: '#ffe5e5',
      dangerItemColor: '#f24c4e',
      dangerItemHoverColor: '#f24c4e',
      dangerItemSelectedBg: '#ffe5e5',
      dangerItemSelectedColor: '#f24c4e',
      darkDangerItemActiveBg: '#f24c4e',
      darkDangerItemColor: '#f24c4e',
      darkDangerItemHoverColor: '#ff9999',
      darkDangerItemSelectedBg: '#f24c4e',
      darkDangerItemSelectedColor: '#ffffff',
    },
    InputNumber: {
      paddingInlineSM: 7,
      paddingInlineLG: 11,
      paddingInline: 11,
      paddingBlockSM: 0,
      paddingBlockLG: 7,
      paddingBlock: 4,
      handleWidth: 22,
      controlWidth: 90,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningBorderHover: '#ffb469',
      colorWarning: '#fa8816',
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimary: '#126e69',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorErrorBorderHover: '#ff9999',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#1ba39c',
      handleHoverColor: '#126e69',
      handleBorderColor: '#ced2d9',
      handleBg: '#ffffff',
      handleActiveBg: 'rgba(12, 12, 13, 0.02)',
      addonBg: 'rgba(12, 12, 13, 0.02)',
      activeBorderColor: '#126e69',
      activeBg: '#ffffff',
      hoverBg: '#ffffff',
      inputFontSize: 14,
      inputFontSizeLG: 16,
      inputFontSizeSM: 12,
      fontFamily: 'Poppins',
      colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorWarningBg: '#fff3e6',
      colorWarningBgHover: '#ffddba',
      colorErrorBg: '#ffe5e5',
      colorErrorBgHover: '#ffdfdf',
      filledHandleBg: '#f0f0f0',
      colorErrorText: '#ff6666',
      colorWarningText: '#fa8816',
    },
    Image: {
      previewOperationSize: 18,
      previewOperationHoverColor: 'rgba(255, 255, 255, 0.85)',
      previewOperationColorDisabled: 'rgba(255, 255, 255, 0.25)',
      previewOperationColor: 'rgba(255, 255, 255, 0.65)',
      paddingXXS: 4,
      paddingSM: 12,
      paddingLG: 24,
      marginXXS: 4,
      marginXL: 32,
      marginSM: 12,
      margin: 16,
      fontSizeIcon: 12,
      controlHeightLG: 40,
      colorTextLightSolid: '#ffffff',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
    },
    Card: {
      headerHeightSM: 38,
      headerHeight: 56,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSize: 14,
      borderRadiusLG: 8,
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimary: '#126e69',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorBorderSecondary: '#e4e8f0',
      colorBgContainer: '#ffffff',
      headerBg: 'rgba(0, 0, 0, 0)',
      headerFontSize: 16,
      headerFontSizeSM: 14,
      fontSizeLG: 16,
      fontFamily: 'Poppins',
      bodyPaddingSM: 12,
      headerPaddingSM: 12,
      bodyPadding: 24,
      headerPadding: 24,
      actionsBg: '#ffffff',
    },
    Carousel: {
      dotWidth: 16,
      dotHeight: 3,
      dotActiveWidth: 24,
      marginXXS: 4,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightSM: 24,
      controlHeightLG: 40,
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorBgContainer: '#ffffff',
      fontFamily: 'Poppins',
      arrowOffset: 8,
      arrowSize: 16,
      dotGap: 4,
      dotOffset: 12,
    },
    Cascader: {
      dropdownHeight: 180,
      controlWidth: 184,
      controlItemWidth: 111,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlInteractiveSize: 16,
      controlHeight: 32,
      borderRadiusSM: 4,
      colorWhite: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      optionSelectedBg: '#d3f8f6',
      menuPadding: 4,
      optionSelectedFontWeight: 600,
      fontFamily: 'Poppins',
      borderRadiusLG: 8,
      optionSelectedColor: 'rgba(12, 12, 13, 0.88)',
    },
    Calendar: {
      yearControlWidth: 80,
      monthControlWidth: 70,
      controlHeight: 32,
      screenXS: 480,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlHeightSM: 24,
      controlHeightLG: 40,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimary: '#126e69',
      colorLinkHover: '#1ba39c',
      colorLinkActive: '#126e69',
      colorLink: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      itemActiveBg: '#d3f8f6',
      fullPanelBg: '#ffffff',
      fullBg: '#ffffff',
      fontFamily: 'Poppins',
      miniContentHeight: 256,
    },
    Button: {
      paddingInlineSM: 7,
      paddingInlineLG: 15,
      paddingInline: 15,
      onlyIconSizeSM: 14,
      onlyIconSizeLG: 18,
      paddingXS: 8,
      paddingContentHorizontal: 16,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidth: 1,
      contentLineHeight: 1.5714285714285714,
      contentFontSizeLG: 16,
      contentFontSize: 14,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimaryActive: '#126e69',
      colorPrimary: '#126e69',
      colorLinkHover: '#1ba39c',
      colorLinkActive: '#126e69',
      colorLink: '#126e69',
      colorErrorOutline: 'rgba(255, 6, 6, 0.06)',
      colorErrorHover: '#ff9999',
      colorErrorBorderHover: '#ff9999',
      colorErrorBg: '#ffe5e5',
      colorErrorActive: '#e61723',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      colorBgTextActive: 'rgba(12, 12, 13, 0.15)',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      textHoverBg: 'rgba(12, 12, 13, 0.06)',
      primaryColor: '#ffffff',
      onlyIconSize: 16,
      linkHoverBg: 'rgba(0, 0, 0, 0)',
      ghostBg: 'rgba(0, 0, 0, 0)',
      defaultGhostColor: '#ffffff',
      defaultGhostBorderColor: '#ffffff',
      defaultColor: 'rgba(12, 12, 13, 0.88)',
      defaultBorderColor: '#ced2d9',
      defaultBg: '#ffffff',
      dangerColor: '#ffffff',
      borderColorDisabled: '#ced2d9',
      defaultHoverBg: '#ffffff',
      defaultHoverColor: '#1ba39c',
      defaultHoverBorderColor: '#1ba39c',
      defaultActiveBg: '#ffffff',
      defaultActiveColor: '#126e69',
      defaultActiveBorderColor: '#126e69',
      fontWeight: 400,
      contentFontSizeSM: 14,
      contentLineHeightLG: 1.5,
      contentLineHeightSM: 1.5714285714285714,
      textTextActiveColor: 'rgba(12, 12, 13, 0.88)',
      textTextHoverColor: 'rgba(12, 12, 13, 0.88)',
      textTextColor: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryBg: '#d3f8f6',
      colorBgSolid: '#0c0c0d',
      colorBgSolidActive: 'rgba(12, 12, 13, 0.95)',
      colorBgSolidHover: 'rgba(12, 12, 13, 0.75)',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      solidTextColor: '#ffffff',
      dashedBgDisabled: 'rgba(0, 0, 0, 0.04)',
    },
    Badge: {
      statusSize: 6,
      indicatorHeight: 20,
      dotSize: 6,
      paddingXS: 8,
      marginXS: 8,
      lineWidth: 1,
      borderRadiusSM: 4,
      fontSizeSM: 12,
      fontSize: 14,
      colorWarning: '#fa8816',
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorPrimary: '#126e69',
      colorErrorHover: '#ff9999',
      colorError: '#f24c4e',
      colorBorderBg: '#ffffff',
      colorBgContainer: '#ffffff',
      indicatorHeightSM: 14,
      textFontSize: 12,
      textFontSizeSM: 12,
      lineHeight: 1.5714285714285714,
      fontFamily: 'Poppins',
    },
    Form: {
      screenXSMax: 575,
      screenSMMax: 767,
      screenMDMax: 991,
      screenLGMax: 1199,
      paddingXS: 8,
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorPrimary: '#126e69',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      labelRequiredMarkColor: '#f24c4e',
      labelColor: 'rgba(12, 12, 13, 0.88)',
      itemMarginBottom: 24,
      labelColonMarginInlineEnd: 8,
      labelColonMarginInlineStart: 2,
      labelHeight: 32,
      labelFontSize: 14,
      fontFamily: 'Poppins',
    },
    Avatar: {
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeXL: 20,
      fontSizeLG: 16,
      fontSizeHeading3: 24,
      fontSize: 14,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorBorderBg: '#ffffff',
      containerSizeSM: 24,
      containerSizeLG: 40,
      containerSize: 32,
      textFontSize: 18,
      textFontSizeLG: 24,
      textFontSizeSM: 14,
      fontFamily: 'Poppins',
      groupBorderColor: '#ffffff',
      groupOverlapping: -8,
      iconFontSize: 18,
      iconFontSizeLG: 24,
      iconFontSizeSM: 14,
    },
    Tour: {
      sizePopupArrow: 16,
      paddingXS: 8,
      padding: 16,
      marginXS: 8,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSize: 14,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimary: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorFill: 'rgba(12, 12, 13, 0.15)',
      colorBgTextHover: 'rgba(12, 12, 13, 0.06)',
      colorBgElevated: '#ffffff',
      closeBtnSize: 22,
      primaryNextBtnHoverBg: '#f0f0f0',
      primaryPrevBtnBg: 'rgba(255, 255, 255, 0.15)',
      fontFamily: 'Poppins',
      lineWidthFocus: 4,
    },
    QRCode: {
      paddingSM: 12,
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeight: 32,
      borderRadiusLG: 8,
      colorWhite: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      fontFamily: 'Poppins',
    },
    Upload: {
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginXL: 32,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeHeading3: 24,
      fontSizeHeading2: 30,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlHeightLG: 40,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimary: '#126e69',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorErrorBg: '#ffe5e5',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      lineWidthFocus: 4,
      fontFamily: 'Poppins',
      actionsColor: 'rgba(12, 12, 13, 0.45)',
      pictureCardSize: 102,
    },
    Typography: {
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightHeading5: 1.5,
      lineHeightHeading4: 1.4,
      lineHeightHeading3: 1.3333333333333333,
      lineHeightHeading2: 1.2666666666666666,
      lineHeightHeading1: 1.2105263157894737,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeHeading5: 16,
      fontSizeHeading4: 20,
      fontSizeHeading3: 24,
      fontSizeHeading2: 30,
      fontSizeHeading1: 38,
      fontSize: 14,
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorLinkHover: '#1ba39c',
      colorLinkActive: '#126e69',
      colorLink: '#126e69',
      colorErrorHover: '#ff9999',
      colorErrorActive: '#e61723',
      colorError: '#f24c4e',
      fontFamilyCode: 'Roboto Mono',
    },
    TreeSelect: {
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlInteractiveSize: 16,
      controlHeightSM: 24,
      borderRadiusSM: 4,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorBorder: '#ced2d9',
      colorBgElevated: '#ffffff',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      titleHeight: 24,
      nodeSelectedBg: '#d3f8f6',
      nodeHoverBg: 'rgba(12, 12, 13, 0.04)',
      fontFamily: 'Poppins',
      borderRadiusLG: 8,
    },
    Tree: {
      paddingSM: 12,
      borderRadiusLG: 8,
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlInteractiveSize: 16,
      controlHeightSM: 24,
      borderRadiusSM: 4,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      titleHeight: 24,
      nodeSelectedBg: '#d3f8f6',
      nodeHoverBg: 'rgba(12, 12, 13, 0.04)',
      directoryNodeSelectedColor: '#ffffff',
      directoryNodeSelectedBg: '#126e69',
      fontFamily: 'Poppins',
      nodeHoverColor: 'rgba(12, 12, 13, 0.88)',
      nodeSelectedColor: 'rgba(12, 12, 13, 0.88)',
      indentSize: 24,
    },
    Tag: {
      paddingXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      borderRadiusSM: 4,
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryActive: '#126e69',
      colorPrimary: '#126e69',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorFillQuaternary: 'rgba(12, 12, 13, 0.02)',
      colorBorder: '#ced2d9',
      colorWarningBorder: '#ffc891',
      colorWarningBg: '#fff3e6',
      colorSuccessBorder: '#c2eb8f',
      colorSuccessBg: '#f7ffed',
      colorInfoBorder: '#8ae4df',
      colorInfoBg: '#d3f8f6',
      colorErrorBorder: '#ffb2b2',
      colorErrorBg: '#ffe5e5',
      defaultColor: 'rgba(12, 12, 13, 0.88)',
      defaultBg: '#f5f5f5',
      fontFamily: 'Poppins',
      solidTextColor: '#ffffff',
      colorBgSolid: '#0c0c0d',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
    },
    Statistic: {
      padding: 16,
      marginXXS: 4,
      lineHeight: 1.5714285714285714,
      fontSizeHeading3: 24,
      fontSize: 14,
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      contentFontSize: 24,
      titleFontSize: 14,
      fontFamily: 'Poppins',
    },
    Skeleton: {
      padding: 16,
      marginXXS: 4,
      marginSM: 12,
      marginLG: 24,
      controlHeightXS: 16,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      colorFillContent: 'rgba(12, 12, 13, 0.06)',
      colorFill: 'rgba(12, 12, 13, 0.15)',
      blockRadius: 4,
      paragraphLiHeight: 16,
      paragraphMarginTop: 28,
      titleHeight: 16,
    },
    Select: {
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlHeightXS: 16,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(255, 130, 5, 0.1)',
      colorWarningHover: '#ffb469',
      colorTextTertiary: 'rgba(12, 12, 13, 0.45)',
      colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimary: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorErrorOutline: 'rgba(255, 6, 6, 0.06)',
      colorErrorHover: '#ff9999',
      colorBorder: '#ced2d9',
      colorBgElevated: '#ffffff',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      singleItemHeightLG: 40,
      selectorBg: '#ffffff',
      optionSelectedColor: 'rgba(12, 12, 13, 0.88)',
      optionSelectedBg: '#d3f8f6',
      optionHeight: 32,
      optionActiveBg: 'rgba(12, 12, 13, 0.04)',
      multipleSelectorBgDisabled: 'rgba(12, 12, 13, 0.04)',
      multipleItemHeightLG: 32,
      multipleItemHeight: 24,
      multipleItemColorDisabled: 'rgba(12, 12, 13, 0.25)',
      multipleItemBorderColorDisabled: 'rgba(0, 0, 0, 0)',
      multipleItemBorderColor: 'rgba(0, 0, 0, 0)',
      multipleItemBg: 'rgba(12, 12, 13, 0.06)',
      clearBg: '#ffffff',
      optionSelectedFontWeight: 600,
      fontFamily: 'Poppins',
      showArrowPaddingInlineEnd: 18,
      activeBorderColor: '#126e69',
      hoverBorderColor: '#1ba39c',
      colorErrorBg: '#ffe5e5',
      colorErrorBgHover: '#ffdfdf',
      colorWarningBg: '#fff3e6',
      colorWarningBgHover: '#ffddba',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorWarning: '#fa8816',
      colorError: '#f24c4e',
      multipleItemHeightSM: 16,
    },
    Result: {
      paddingXS: 8,
      paddingXL: 32,
      paddingLG: 24,
      padding: 16,
      marginXS: 8,
      lineHeightHeading3: 1.3333333333333333,
      lineHeight: 1.5714285714285714,
      fontSizeHeading3: 24,
      fontSize: 14,
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorSuccess: '#78c41a',
      colorInfo: '#126e69',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorError: '#f24c4e',
      iconFontSize: 72,
      subtitleFontSize: 14,
      titleFontSize: 24,
    },
    Progress: {
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSize: 14,
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorError: '#f24c4e',
      colorBgContainer: '#ffffff',
      remainingColor: 'rgba(12, 12, 13, 0.06)',
      defaultColor: '#126e69',
      circleTextColor: 'rgba(12, 12, 13, 0.88)',
      fontFamily: 'Poppins',
      lineBorderRadius: 100,
      colorWhite: '#ffffff',
    },
    Popconfirm: {
      marginXXS: 4,
      marginXS: 8,
      fontWeightStrong: 600,
      fontSize: 14,
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorText: 'rgba(12, 12, 13, 0.88)',
    },
    Pagination: {
      screenSM: 576,
      screenLG: 992,
      paddingXXS: 4,
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      margin: 16,
      lineWidthFocus: 4,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlItemBgActiveDisabled: 'rgba(12, 12, 13, 0.15)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(255, 130, 5, 0.1)',
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorErrorOutline: 'rgba(255, 6, 6, 0.06)',
      colorBorder: '#ced2d9',
      colorBgTextHover: 'rgba(12, 12, 13, 0.06)',
      colorBgTextActive: 'rgba(12, 12, 13, 0.15)',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      itemSizeSM: 24,
      itemSize: 32,
      itemLinkBg: '#ffffff',
      itemInputBg: '#ffffff',
      itemBg: '#ffffff',
      itemActiveColorDisabled: 'rgba(12, 12, 13, 0.25)',
      itemActiveBgDisabled: 'rgba(12, 12, 13, 0.15)',
      itemActiveBg: '#ffffff',
      fontFamily: 'Poppins',
      itemActiveColor: '#126e69',
      itemActiveColorHover: '#1ba39c',
      miniOptionsSizeChangerTop: 0,
      itemSizeLG: 40,
    },
    Modal: {
      screenSMMax: 767,
      paddingXS: 8,
      paddingMD: 20,
      paddingLG: 24,
      paddingContentHorizontalLG: 24,
      padding: 16,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineWidthFocus: 4,
      lineWidth: 1,
      lineHeightHeading5: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      fontSizeHeading5: 16,
      fontSize: 14,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorPrimaryBorder: '#8ae4df',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      titleColor: 'rgba(12, 12, 13, 0.88)',
      headerBg: '#ffffff',
      footerBg: 'rgba(0, 0, 0, 0)',
      contentBg: '#ffffff',
      titleFontSize: 16,
      titleLineHeight: 1.375,
      fontFamily: 'Poppins',
    },
    Message: {
      paddingXS: 8,
      paddingSM: 12,
      marginXS: 8,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlHeightLG: 40,
      borderRadiusLG: 8,
      colorWarning: '#fa8816',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccess: '#78c41a',
      colorInfo: '#126e69',
      colorError: '#f24c4e',
      contentBg: '#ffffff',
      fontFamily: 'Poppins',
    },
    List: {
      screenSM: 576,
      screenMD: 768,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      paddingContentVerticalSM: 8,
      paddingContentVerticalLG: 16,
      paddingContentVertical: 12,
      paddingContentHorizontalLG: 24,
      paddingContentHorizontal: 16,
      padding: 16,
      marginXXS: 4,
      marginXXL: 48,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusLG: 8,
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimary: '#126e69',
      colorBorder: '#ced2d9',
      headerBg: 'rgba(0, 0, 0, 0)',
      footerBg: 'rgba(0, 0, 0, 0)',
      avatarMarginRight: 16,
      descriptionFontSize: 14,
      fontFamily: 'Poppins',
    },
    FloatButton: {
      paddingXXS: 4,
      marginXXL: 48,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlHeightLG: 40,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimaryHover: '#1ba39c',
      colorPrimary: '#126e69',
      colorFillContent: 'rgba(12, 12, 13, 0.06)',
      colorBgElevated: '#ffffff',
      fontFamily: 'Poppins',
    },
    Empty: {
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      fontSize: 14,
      lineHeight: 1.5714285714285714,
    },
    Dropdown: {
      sizePopupArrow: 16,
      paddingXXS: 4,
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      controlPaddingHorizontal: 12,
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActiveHover: '#bdf0ed',
      controlItemBgActive: '#d3f8f6',
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorError: '#f24c4e',
      colorBgElevated: '#ffffff',
      paddingBlock: 5,
      fontFamily: 'Poppins',
    },
    Drawer: {
      paddingXS: 8,
      paddingLG: 24,
      padding: 16,
      marginSM: 12,
      lineWidth: 1,
      lineHeightLG: 1.5,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      colorBgElevated: '#ffffff',
      lineWidthFocus: 4,
      footerPaddingBlock: 8,
      footerPaddingInline: 16,
    },
    Divider: {
      marginXS: 8,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      fontSizeLG: 16,
      fontSize: 14,
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      lineHeight: 1.5714285714285714,
      fontFamily: 'Poppins',
      verticalMarginInline: 8,
    },
    Descriptions: {
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      borderRadiusLG: 8,
      colorTextTertiary: 'rgba(12, 12, 13, 0.45)',
      colorTextSecondary: 'rgba(12, 12, 13, 0.65)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      titleColor: 'rgba(12, 12, 13, 0.88)',
      labelBg: 'rgba(12, 12, 13, 0.02)',
      contentColor: 'rgba(12, 12, 13, 0.88)',
      fontFamily: 'Poppins',
      labelColor: 'rgba(12, 12, 13, 0.45)',
      colonMarginLeft: 2,
      colonMarginRight: 8,
      itemPaddingBottom: 16,
      itemPaddingEnd: 16,
      titleMarginBottom: 20,
    },
    DatePicker: {
      sizePopupArrow: 16,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.1)',
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      controlItemBgActive: '#d3f8f6',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(255, 130, 5, 0.1)',
      colorWarningHover: '#ffb469',
      colorWarning: '#fa8816',
      colorTextQuaternary: 'rgba(12, 12, 13, 0.25)',
      colorTextPlaceholder: 'rgba(12, 12, 13, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorLinkHover: '#1ba39c',
      colorLinkActive: '#126e69',
      colorLink: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorErrorOutline: 'rgba(255, 6, 6, 0.06)',
      colorErrorHover: '#ff9999',
      colorError: '#f24c4e',
      colorBorder: '#ced2d9',
      colorBgElevated: '#ffffff',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#1ba39c',
      cellHoverBg: 'rgba(12, 12, 13, 0.04)',
      cellHeight: 24,
      activeBorderColor: '#126e69',
      paddingInline: 11,
      paddingInlineSM: 7,
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorErrorBg: '#ffe5e5',
      colorWarningBg: '#fff3e6',
      colorWarningText: '#fa8816',
      colorErrorText: '#ff6666',
      colorWarningBgHover: '#ffddba',
      colorErrorBgHover: '#ffdfdf',
      activeBg: '#ffffff',
      hoverBg: '#ffffff',
      cellBgDisabled: 'rgba(12, 12, 13, 0.04)',
      cellActiveWithRangeBg: '#d3f8f6',
      cellHoverWithRangeBg: '#cbe0fd',
      cellRangeBorderColor: '#82b4f9',
      multipleItemBg: 'rgba(12, 12, 13, 0.06)',
      multipleItemBorderColor: 'rgba(0, 0, 0, 0)',
      multipleItemBorderColorDisabled: 'rgba(0, 0, 0, 0)',
      multipleItemColorDisabled: 'rgba(12, 12, 13, 0.25)',
      multipleSelectorBgDisabled: 'rgba(12, 12, 13, 0.04)',
      cellWidth: 36,
      multipleItemHeight: 24,
      multipleItemHeightLG: 32,
      multipleItemHeightSM: 16,
      paddingBlock: 4,
      paddingBlockLG: 7,
      paddingBlockSM: 0,
      paddingInlineLG: 11,
      presetsMaxWidth: 200,
      presetsWidth: 120,
      textHeight: 40,
      timeCellHeight: 28,
      timeColumnHeight: 224,
      timeColumnWidth: 56,
      withoutTimeCellHeight: 66,
      inputFontSize: 14,
      inputFontSizeLG: 16,
      inputFontSizeSM: 12,
      fontFamily: 'Poppins',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
    },
    Collapse: {
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginSM: 12,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      borderRadiusLG: 8,
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorFillAlter: 'rgba(12, 12, 13, 0.02)',
      colorBorder: '#ced2d9',
      colorBgContainer: '#ffffff',
      headerBg: 'rgba(12, 12, 13, 0.02)',
      contentBg: '#ffffff',
      lineHeightLG: 1.5,
      fontFamily: 'Poppins',
      borderlessContentBg: 'rgba(0, 0, 0, 0)',
    },
    Checkbox: {
      paddingXS: 8,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlInteractiveSize: 16,
      borderRadiusSM: 4,
      colorWhite: '#ffffff',
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorPrimaryHover: '#1ba39c',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorBorder: '#ced2d9',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      fontFamily: 'Poppins',
    },
    Breadcrumb: {
      paddingXXS: 4,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineHeight: 1.5714285714285714,
      fontSizeIcon: 12,
      fontSize: 14,
      borderRadiusSM: 4,
      colorPrimaryBorder: '#8ae4df',
      colorBgTextHover: 'rgba(12, 12, 13, 0.06)',
      separatorColor: 'rgba(12, 12, 13, 0.45)',
      linkHoverColor: 'rgba(12, 12, 13, 0.88)',
      linkColor: 'rgba(12, 12, 13, 0.45)',
      lastItemColor: 'rgba(12, 12, 13, 0.88)',
      itemColor: 'rgba(12, 12, 13, 0.45)',
      separatorMargin: 8,
      iconFontSize: 14,
      fontFamily: 'Poppins',
    },
    Anchor: {
      paddingXXS: 4,
      lineWidthBold: 2,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSplit: 'rgba(12, 12, 13, 0.06)',
      colorPrimary: '#126e69',
      linkPaddingInlineStart: 16,
      linkPaddingBlock: 4,
      fontFamily: 'Poppins',
    },
    Alert: {
      paddingMD: 20,
      paddingContentVerticalSM: 8,
      paddingContentHorizontalLG: 24,
      marginXS: 8,
      marginSM: 12,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSizeHeading3: 24,
      fontSize: 14,
      borderRadiusLG: 8,
      colorWarningBorder: '#ffc891',
      colorWarningBg: '#fff3e6',
      colorWarning: '#fa8816',
      colorTextHeading: 'rgba(12, 12, 13, 0.88)',
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorSuccessBorder: '#c2eb8f',
      colorSuccessBg: '#f7ffed',
      colorSuccess: '#78c41a',
      colorInfoBorder: '#8ae4df',
      colorInfoBg: '#d3f8f6',
      colorInfo: '#126e69',
      colorIconHover: 'rgba(12, 12, 13, 0.88)',
      colorIcon: 'rgba(12, 12, 13, 0.45)',
      colorErrorBorder: '#ffb2b2',
      colorErrorBg: '#ffe5e5',
      colorError: '#f24c4e',
      withDescriptionIconSize: 24,
      fontFamily: 'Poppins',
    },
    Space: {
      paddingXS: 8,
      paddingLG: 24,
      padding: 16,
    },
    Layout: {
      bodyBg: '#f5f5f5',
      footerBg: '#f5f5f5',
      headerBg: '#001529',
      headerColor: 'rgba(12, 12, 13, 0.88)',
      lightSiderBg: '#ffffff',
      lightTriggerBg: '#ffffff',
      lightTriggerColor: 'rgba(12, 12, 13, 0.88)',
      siderBg: '#001529',
      triggerBg: '#002140',
      triggerColor: '#ffffff',
      colorText: 'rgba(12, 12, 13, 0.88)',
      headerHeight: 64,
      triggerHeight: 48,
      zeroTriggerHeight: 40,
      zeroTriggerWidth: 40,
      borderRadius: 6,
      fontSize: 14,
      fontSizeXL: 20,
    },
    Mentions: {
      fontFamily: 'Poppins',
      fontSize: 14,
      lineHeight: 1.5714285714285714,
      controlItemWidth: 100,
    },
    Splitter: {
      lineWidth: 1,
      controlHeightLG: 40,
      controlHeight: 32,
      colorTextDisabled: 'rgba(12, 12, 13, 0.25)',
      colorTextDescription: 'rgba(12, 12, 13, 0.45)',
      colorPrimaryBorderHover: '#23cdc4',
      colorPrimaryBorder: '#8ae4df',
      colorPrimary: '#126e69',
      colorFillTertiary: 'rgba(12, 12, 13, 0.04)',
      colorFillSecondary: 'rgba(12, 12, 13, 0.06)',
      colorFillContentHover: 'rgba(12, 12, 13, 0.15)',
      colorBorderSecondary: '#e4e8f0',
      colorBgElevated: '#ffffff',
      colorBgContainerDisabled: 'rgba(12, 12, 13, 0.04)',
      colorBgContainer: '#ffffff',
      splitBarDraggableSize: 20,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      fontFamily: 'Poppins',
      controlHeightSM: 24,
      borderRadiusXS: 2,
      colorText: 'rgba(12, 12, 13, 0.88)',
      colorFill: 'rgba(12, 12, 13, 0.15)',
      controlItemBgActive: '#d3f8f6',
      controlItemBgActiveHover: '#bdf0ed',
      controlItemBgHover: 'rgba(12, 12, 13, 0.04)',
      fontSizeSM: 12,
      splitBarSize: 2,
      splitTriggerSize: 6,
    },
  },
} satisfies ThemeConfig;
